import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import {
  buildHarvestContactPayload,
  buildHarvestLocationsEndpoint,
} from '../utils/harvestLocations';

function HarvestForm({ organizationSlug, onContactAdded }) {
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    email: '',
    harvest_location_id: '',
    custom_location: '',
    evangelism_date: new Date(),
    collector_name: '',
    collector_phone: ''
  });

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  useEffect(() => {
    if (organizationSlug) fetchLocations();
  }, [organizationSlug]);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(buildHarvestLocationsEndpoint(organizationSlug), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setMessage({ type: 'error', text: 'Failed to load locations' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Function to check for duplicates
  const checkForDuplicate = async (data) => {
    try {
      setIsCheckingDuplicate(true);
      setDuplicateInfo(null);
      
      const checkData = {
        email: data.email || '',
        phone_number: data.phone_number || '',
        evangelism_date: data.evangelism_date,
        organization_slug: organizationSlug,
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/harvest/contacts/check-duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(checkData),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.isDuplicate) {
          setDuplicateInfo(result.duplicate);
          return true; // Duplicate found
        }
      }
      return false; // No duplicate
    } catch (error) {
      console.error('Error checking for duplicate:', error);
      return false; // Continue with submission if check fails
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone_number || !formData.evangelism_date) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    // Check if either a location is selected OR "Other" is chosen with custom name
    if (!formData.harvest_location_id && !(formData.harvest_location_id === 'other' && formData.custom_location)) {
      setMessage({ type: 'error', text: 'Please select a location or choose "Other" with a custom location name' });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: '', text: '' });
      setDuplicateInfo(null);
      
      // First check for duplicates
      const isDuplicate = await checkForDuplicate(formData);
      if (isDuplicate) {
        return; // Stop submission if duplicate found
      }

      const submissionData = buildHarvestContactPayload(formData, organizationSlug, locations);

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/harvest/contacts`, submissionData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setMessage({ type: 'success', text: 'Contact added successfully!' });
      
      // Reset form
      setFormData({
        name: '',
        phone_number: '',
        email: '',
        harvest_location_id: '',
        custom_location: '',
        evangelism_date: new Date(),
        collector_name: '',
        collector_phone: ''
      });

      // Trigger refresh in parent component
      if (onContactAdded) {
        onContactAdded();
      }
    } catch (error) {
      console.error('Error adding contact:', error);
      
      // Handle duplicate error from backend
      if (error.response?.status === 409) {
        setDuplicateInfo(error.response.data.duplicate);
        setMessage({ type: 'error', text: error.response.data.message || 'This contact has already been registered for this date.' });
        return;
      }
      
      const errorMessage = error.response?.data?.error || error.message || 'Failed to add contact';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Add New Harvest Contact
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      {duplicateInfo && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Duplicate Contact Detected
          </Typography>
          <Typography variant="body2">
            A contact already exists for <strong>{duplicateInfo.name}</strong> on <strong>{duplicateInfo.evangelism_date}</strong>.
          </Typography>
          {duplicateInfo.email && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Email: {duplicateInfo.email}
            </Typography>
          )}
          {duplicateInfo.phone && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Phone: {duplicateInfo.phone}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
            If this is a different person, please contact the church office for assistance.
          </Typography>
        </Alert>
      )}

      {isCheckingDuplicate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Checking for existing contacts...
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Contact Name *"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone Number *"
              value={formData.phone_number}
              onChange={(e) => handleInputChange('phone_number', e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email (Optional)"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              options={locations.filter(loc => loc.name !== 'Other')}
              getOptionLabel={(option) => option.name}
              value={locations.find(loc => loc.id === formData.harvest_location_id) || null}
              onChange={(event, newValue) => {
                if (newValue) {
                  handleInputChange('harvest_location_id', newValue.id);
                } else {
                  handleInputChange('harvest_location_id', '');
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Harvest Location *"
                  required={formData.harvest_location_id !== 'other'}
                  placeholder="Search or select location"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography>{option.name}</Typography>
                </li>
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Or Select Other</InputLabel>
              <Select
                value={formData.harvest_location_id === 'other' ? 'other' : ''}
                onChange={(e) => {
                  if (e.target.value === 'other') {
                    handleInputChange('harvest_location_id', 'other');
                  } else {
                    handleInputChange('harvest_location_id', '');
                  }
                }}
                label="Or Select Other"
              >
                <MenuItem value="other">Other (Custom Location)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {formData.harvest_location_id === 'other' && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Custom Location Name *"
                value={formData.custom_location || ''}
                onChange={(e) => handleInputChange('custom_location', e.target.value)}
                placeholder="Enter location name"
                required
              />
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Evangelism Date *"
                value={formData.evangelism_date}
                onChange={(date) => handleInputChange('evangelism_date', date)}
                renderInput={(params) => <TextField {...params} fullWidth required />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Collector Name (Optional)"
              value={formData.collector_name}
              onChange={(e) => handleInputChange('collector_name', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Collector Phone (Optional)"
              value={formData.collector_phone}
              onChange={(e) => handleInputChange('collector_phone', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || isCheckingDuplicate}
                startIcon={(submitting || isCheckingDuplicate) ? <CircularProgress size={20} /> : null}
              >
                {isCheckingDuplicate ? 'Checking...' : (submitting ? 'Adding...' : 'Add Contact')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}

export default HarvestForm;
